import React from "react";
import { motion } from "framer-motion";
import { Shield } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-white font-['Inter']" data-testid="privacy-page">
      <Header />

      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500/30 to-purple-500/30 rounded-full flex items-center justify-center mx-auto mb-6 backdrop-blur-sm border border-white/10">
              <Shield className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Privacy <span className="gradient-text">Policy</span>
            </h1>
            <p className="text-gray-300">Last updated: April 28, 2026</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 prose prose-gray max-w-none">
          <div className="space-y-8">
            <div>
              <p className="text-gray-600">
                <b>Digital Master Media LLC</b> (&ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;) is committed to protecting your privacy.
                This Privacy Policy describes how we handle information in connection with our services.
              </p>
              <p className="text-gray-600 mt-3">
                By accessing or using our website and services, you agree to this Privacy Policy.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Purpose</h2>
              <p className="text-gray-600">
                This Privacy Policy outlines how information is used to operate our platform, maintain security,
                and comply with applicable laws (including GDPR, CCPA, the India DPDP Act and Wyoming consumer-protection statutes).
                It is incorporated by reference into our{" "}
                <a href="/terms" className="text-blue-600 underline">Terms of Service</a>{" "}
                and the Master Publisher Agreement.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Use of Information</h2>
              <p className="text-gray-600 mb-2">We use information to:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Operate and manage our services</li>
                <li>Verify users (KYC) and prevent fraud or duplicate accounts</li>
                <li>Process transactions and maintain accounts</li>
                <li>Send transactional emails (status updates, signed Agreement) and, while your account is active, broadcast emails about new offers</li>
                <li>Improve platform performance and security</li>
                <li>Comply with legal obligations and respond to lawful requests</li>
              </ul>
              <p className="text-gray-600 mt-3 text-sm">
                <b>Lawful basis (GDPR / DPDP):</b> your explicit consent for ID processing,
                performance of a contract, our legitimate interests in fraud prevention, and legal obligations.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Sharing of Information</h2>
              <p className="text-gray-600 mb-2">We may share information with:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><b>Advertisers and buyers</b> for service fulfilment (call routing, lead delivery)</li>
                <li><b>Third-party sub-processors</b> supporting operations:
                  <ul className="list-disc pl-6 mt-1 space-y-1">
                    <li>Cloudinary, Inc. (USA / EU) &mdash; encrypted storage of uploaded ID images and files</li>
                    <li>Google LLC &mdash; Gemini API (USA) &mdash; automated visual ID verification</li>
                    <li>Resend, Inc. (USA) &mdash; transactional and marketing email delivery</li>
                    <li>MongoDB Atlas / Emergent &mdash; managed database hosting</li>
                  </ul>
                </li>
                <li><b>Legal authorities</b> when required by law, court order, or to protect our rights</li>
              </ul>
              <p className="text-gray-600 mt-3">
                We do <b>not</b> sell personal information to unauthorised third parties.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Security</h2>
              <p className="text-gray-600">
                We implement reasonable technical and organisational safeguards to protect information,
                including TLS&nbsp;1.2+ in transit, AES-256 encryption at rest on Cloudinary and our database,
                role-based access controls, multi-factor authentication on admin accounts, and continuous logging.
                Uploaded ID images are watermarked &ldquo;FOR VERIFICATION PURPOSE ONLY&rdquo; before storage so they cannot be re-purposed elsewhere.
                However, no system is completely secure, and we cannot guarantee absolute security.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Retention</h2>
              <p className="text-gray-600 mb-2">
                Information is retained only as long as necessary for business, legal, and compliance purposes:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><b>ID images &amp; KYC artefacts:</b> deleted <b>within 7 days</b> after you leave the network or your account is terminated, whichever happens first.</li>
                <li><b>Master Publisher Agreement &amp; signed terms (without the ID image):</b> retained for the lifetime of any potential legal claim (typically 7+ years), for legal-defence and audit purposes only.</li>
                <li><b>Email &amp; marketing data:</b> while your account is active + <b>12&nbsp;months</b> after termination, unless you opt out earlier.</li>
                <li><b>Server logs &amp; technical data:</b> rolling 90&nbsp;days, longer if retained for security investigations.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. User Rights</h2>
              <p className="text-gray-600 mb-2">Subject to applicable law, you may request:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><b>Access</b> &mdash; a copy of the personal data we hold about you</li>
                <li><b>Correction</b> &mdash; rectify inaccurate or incomplete data</li>
                <li><b>Deletion</b> &mdash; erase your data, subject to our legal record-keeping duties</li>
                <li><b>Portability</b> &mdash; receive your data in a structured machine-readable format</li>
                <li><b>Withdraw consent</b> &mdash; for ID processing (terminates the Agreement going forward)</li>
                <li><b>Lodge a complaint</b> with your local data-protection authority (ICO, EU DPAs, FTC, etc.)</li>
              </ul>
              <p className="text-gray-600 mt-3">
                To exercise any of these rights, email{" "}
                <a href="mailto:abidali@digitalmastermedia.com" className="text-blue-600 underline">abidali@digitalmastermedia.com</a>.
                We respond within <b>30 days</b>.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Third-Party Services</h2>
              <p className="text-gray-600">
                Our website and services may rely on or link to third-party services and websites (including the
                sub-processors listed in Section&nbsp;3). We are not responsible for the privacy practices or content
                of any third party. We encourage you to review their privacy policies separately.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. International Transfers</h2>
              <p className="text-gray-600">
                We are based in the United States. If you submit data from outside the US (EU, UK, India and others),
                your data will be transferred to and processed in the US under the EU Standard Contractual Clauses,
                the UK International Data Transfer Addendum, or another lawful transfer mechanism applicable in your jurisdiction.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Children</h2>
              <p className="text-gray-600">
                Our service is not directed at and not for use by anyone under the age of 18.
                We do not knowingly collect data from minors.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Updates</h2>
              <p className="text-gray-600">
                We may update this Privacy Policy at any time. Material changes will be notified by email and / or by posting
                an updated version on this page with a new &ldquo;Last updated&rdquo; date. Continued use of the platform after
                changes take effect constitutes acceptance of the updated policy.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact</h2>
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6">
                <p className="text-gray-900 font-bold mb-2">Digital Master Media LLC</p>
                <p className="text-gray-600">30 N Gould Street, Suite R</p>
                <p className="text-gray-600">Sheridan, WY 82801, USA</p>
                <p className="text-gray-600">Email: abidali@digitalmastermedia.com</p>
                <p className="text-gray-600">Phone: +1 (855) 471-2511</p>
                <p className="text-gray-600">Website: digitalmastermedia.com</p>
              </div>
              <p className="text-gray-500 text-sm mt-4">
                This page is incorporated by reference into the Master Publisher Agreement and our{" "}
                <a href="/terms" className="text-blue-600 underline">Terms of Service</a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
